import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import BlogList from '@/components/blog/BlogList'
import { getArticles, getCategories } from '@/lib/sanity.server'
import CTA from '@/components/LandingPage/CTA'
import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'

const parsePositivePage = (value: unknown) => {
  const page = typeof value === "number" ? value : typeof value === "string" ? Number(value) : 1
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const parseOptionalString = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : undefined)

export const Route = createFileRoute('/blog')({
  validateSearch: (search) => ({
    page: parsePositivePage(search.page),
    q: parseOptionalString(search.q),
    category: parseOptionalString(search.category),
  }),
  loaderDeps: ({ search: { page, q, category } }) => ({ page, q, category }),
  loader: async ({ deps: { page, q, category } }) => {
    const [{ articles, pagination }, categories] = await Promise.all([
      getArticles({ data: { page, pageSize: 9, query: q, category } }),
      getCategories(),
    ])
    // If the requested page is beyond the last page and there are articles,
    // clamp to the last page so users don't see an empty grid.
    if (articles.length === 0 && pagination.total > 0 && page > 1) {
      const clamped = await getArticles({ data: { page: pagination.pageCount, pageSize: 9, query: q, category } })
      return { articles: clamped.articles, pagination: clamped.pagination, q, categories, category }
    }
    return { articles, pagination, q, categories, category }
  },
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: 'Blog | Zapify - AI-Powered Instagram Comment Management',
        description: 'Read the latest insights, tips, and updates about Instagram automation, AI-powered engagement, and social media management.',
        url: 'https://zapify.pro/blog',
        author: 'Zapify Team',
      }),
      { property: "og:image", content: "https://zapify.pro/zapify-ogp.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Zapify Blog" },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/blog" }
    ],
    scripts: [
      createWebPageSchema({
        name: 'Blog | Zapify',
        description: 'Read the latest insights, tips, and updates about Instagram automation, AI-powered engagement, and social media management.',
        url: 'https://zapify.pro/blog',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          {
            position: 1,
            name: 'Home',
            item: 'https://zapify.pro',
          },
          {
            position: 2,
            name: 'Blog',
            item: 'https://zapify.pro/blog',
          },
        ],
      }),
      {
        type: 'application/ld+json' as const,
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Zapify Blog',
          description: 'Insights, tips, and updates about Instagram automation and AI-powered engagement',
          url: 'https://zapify.pro/blog',
          publisher: {
            '@type': 'Organization',
            name: 'Zapify',
            logo: {
              '@type': 'ImageObject',
              url: 'https://zapify.pro/logo.png',
            },
          },
        }),
      },
    ],
  }),
  component: BlogRouteComponent,
})

function BlogRouteComponent() {
  const { articles, pagination, categories, category } = Route.useLoaderData()
  const navigate = useNavigate({ from: "/blog" })

  // If the user lands on a page beyond the last page (e.g. /blog?page=4
  // when there are only 3 pages), redirect to page 1.
  useEffect(() => {
    if (articles.length === 0 && pagination.page > 1) {
      navigate({ search: { page: 1 } })
    }
  }, [articles.length, pagination.page, navigate])

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors">
      <BlogList
        articles={articles}
        pagination={pagination}
        categories={categories}
        activeCategory={category}
      />
      <CTA />
    </div>
  )
}
